import CheckIcon from '@mui/icons-material/Check'
import { McVerificationStatus } from '@numeo/types-core'
import Dropdown from '@mui/joy/Dropdown'
import Menu from '@mui/joy/Menu'
import MenuButton from '@mui/joy/MenuButton'
import MenuItem from '@mui/joy/MenuItem'
import axios from 'axios'
import { useAuth } from 'hooks/auth'
import { useDispatch } from 'react-redux'
import { setOrganizationData } from 'store/authSlice'
import { isCompanyRecordMcVerified } from '@/pages/dashboard/pages/settings/components/Tabs/GeneralTab/formData'
import { clearSignupGates, setAuthToken, stripEntityIdFromPath, switchAuthToken } from 'setup/SetupAxios'
import { useEffect, useRef, useState } from 'react'
import { mcVerificationApi } from 'pages/dashboard/pages/settings/api/mcVerificationApi'
import { AddCompanyModal } from './AddCompanyModal'
import { resolveAddCompanyState } from '../utils/company-add-state'
import { useActiveCompany } from '../hooks/useActiveCompany'
import { syncMcVerificationToApplication, syncMcVerificationToOrganization } from '../utils/mcVerificationSync'

const ChevronUpDown = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M5.5 6.5L8 4L10.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M5.5 9.5L8 12L10.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const AVATAR_COLORS = ['#c7d4ff', '#b4f5cc', '#ddd4f9', '#fdd8a8', '#fbc4c4', '#bae6fd', '#c4b5fd', '#fecaca']

const getAvatarColor = (name: string) => {
    let hash = 0
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

/**
 * MC verification state for a company summary. The badge is only valid while
 * the verified MC still matches the company's current MC.
 */
enum McState {
    Verified = 'verified',
    Pending = 'pending',
    Unverified = 'unverified',
}
type CompanyLike = { mcNumber?: string; mcVerification?: { status?: string; verifiedMcNumber?: string } } | null | undefined
const mcVerificationState = (company: CompanyLike): McState | null => {
    if (!company?.mcNumber) return null
    const v = company.mcVerification
    // Shared predicate (trim + digit-normalized) so the switcher, the settings form, and the header
    // badge agree on verified state instead of the previous untrimmed `===` diverging from them.
    if (isCompanyRecordMcVerified(company)) return McState.Verified
    // "In review" reflects submitted documents only (PENDING_REVIEW). A bare email
    // OTP in flight (PENDING_EMAIL, no docs) is not "in review" — keep nudging to verify.
    if (v?.status === McVerificationStatus.PENDING_REVIEW) return McState.Pending
    return McState.Unverified
}

const McStateDot = ({ state }: { state: McState | null }) => {
    if (!state) return null
    const title = state === McState.Verified ? 'MC verified' : state === McState.Pending ? 'MC verification in review' : 'MC not verified — verify in Settings'
    const color = state === McState.Verified ? '#16a34a' : state === McState.Pending ? '#3b82f6' : '#f59e0b'
    return (
        <span
            title={title}
            style={{
                display: 'inline-block',
                width: 7,
                height: 7,
                borderRadius: 4,
                flexShrink: 0,
                backgroundColor: color,
            }}
        />
    )
}

export const CompanySwitcher = ({ collapsed = false }: { collapsed?: boolean }) => {
    const dispatch = useDispatch()
    const { application, organizationData, setApplication } = useAuth()
    const [addModalOpen, setAddModalOpen] = useState(false)
    const [switching, setSwitching] = useState(false)
    const [switchError, setSwitchError] = useState('')
    const abortRef = useRef<AbortController | null>(null)
    const statusAbortRef = useRef<AbortController | null>(null)
    const organizationDataRef = useRef(organizationData)
    organizationDataRef.current = organizationData
    const applicationRef = useRef(application)
    applicationRef.current = application
    const setApplicationRef = useRef(setApplication)
    setApplicationRef.current = setApplication

    useEffect(() => {
        return () => {
            abortRef.current?.abort()
            statusAbortRef.current?.abort()
        }
    }, [])

    const organization = organizationData ?? null
    const applications = organizationData?.applications ?? []
    const isOwner = organizationData?.isOwner ?? false
    const multiMcEnabled = organization?.multiMcEnabled ?? false
    // The company-add cap is org-scoped (equals the plan's email-connection entitlement) and is
    // provided by the backend on organizationData — the active app's subscription is not carried on
    // the auth payload, so we must read it here. `null` means unlimited (e.g. ULTRA). Backend
    // addCompany enforces the same rule; this only gates button visibility.
    const companiesLimit = organization?.companiesLimit ?? null
    // `companiesLimit === null` = unlimited (ULTRA). Over the cap we show an upgrade
    // prompt rather than hiding the row, so owners understand why they can't add.
    const addState = resolveAddCompanyState({ isOwner, companiesLimit, appCount: applications.length })

    const currentAppId = application?.id
    const hasMultipleApps = applications.length > 1

    // `liveCompany` stays local because the MC-verification badge needs the raw company record,
    // not just the resolved name. useActiveCompany owns the name/MC resolution order.
    const liveCompany = application?.settings?.company
    const { companyName, mcNumber } = useActiveCompany()

    useEffect(() => {
        if (!currentAppId || !liveCompany?.mcNumber) return

        statusAbortRef.current?.abort()
        const controller = new AbortController()
        statusAbortRef.current = controller

        void mcVerificationApi
            .getStatus()
            .then((result) => {
                if (controller.signal.aborted) return

                const company = applicationRef.current?.settings?.company
                const applicationId = applicationRef.current?.id
                setApplicationRef.current((prev) => syncMcVerificationToApplication(prev, result))
                dispatch(
                    setOrganizationData(
                        syncMcVerificationToOrganization(
                            organizationDataRef.current,
                            applicationId,
                            result,
                            company
                                ? {
                                      name: company.name,
                                      mcNumber: company.mcNumber,
                                      dotNumber: company.dotNumber,
                                  }
                                : null
                        )
                    )
                )
            })
            .catch((error) => {
                if (controller.signal.aborted) return
                console.warn('[CompanySwitcher] Failed to refresh MC verification status', error)
            })

        return () => controller.abort()
    }, [currentAppId, dispatch, liveCompany?.mcNumber])

    if (!multiMcEnabled || !organization) return null

    if (!hasMultipleApps && !isOwner) return null

    const handleSwitch = async (targetAppId: string, landingPath?: string) => {
        if (targetAppId === currentAppId || switching) return

        // Cancel any in-flight switch — only the latest click wins
        abortRef.current?.abort()
        const controller = new AbortController()
        abortRef.current = controller

        setSwitching(true)
        setSwitchError('')
        try {
            const res = await axios.post<{ success: boolean; data: { token: string } }>(
                '/organizations/switch',
                { applicationId: targetAppId },
                { signal: controller.signal }
            )

            // Server returned but without a token — treat as a hard failure, never mutate local state
            if (!res.data.success || !res.data.data?.token) {
                setSwitchError('Failed to switch company')
                setSwitching(false)
                return
            }

            // Per-company rollout gate (see FeatureType.PerTabCompanySwitch): the TARGET company
            // decides whether this switch gets the new per-tab session isolation or the
            // pre-existing global switch. Not gated on the CURRENTLY active company — a user can
            // switch between a flagged and an unflagged company in the same session.
            const targetApp = applications.find((a) => a.id === targetAppId)
            const usePerTabSwitch = targetApp?.perTabCompanySwitchEnabled === true

            // Commit order matters:
            //   1. swap the auth token
            //   2. hard navigate to current section (replace, not reload) so the back button can't show stale UI
            //      and the full JS context is rebuilt — no stale Redux/React state survives
            if (usePerTabSwitch) {
                switchAuthToken(res.data.data.token)
            } else {
                // Pre-PR behavior: a global, cross-tab switch via localStorage — every other open
                // tab picks this up through the existing `storage` listener (useAuthRedux) and
                // reloads onto the new company, same as before per-tab isolation existed.
                setAuthToken(res.data.data.token)
            }
            clearSignupGates()
            window.location.replace(landingPath || stripEntityIdFromPath())
        } catch (err) {
            if (axios.isCancel(err)) return
            const msg = axios.isAxiosError(err) ? err.response?.data?.message || 'Switch failed' : 'Switch failed'
            setSwitchError(msg)
            setSwitching(false)
        }
    }

    const menuContent = (
        <Menu
            // The switcher sits at the TOP of the sidebar, so the menu must open DOWNWARD. With the
            // previous `top-start` it anchored to the trigger's top and grew upward off the screen —
            // Popper did not flip — so with many companies (multi-MC owners) the header + company list
            // were clipped above the viewport and only the last item ("Add New Company") stayed visible.
            placement="bottom-start"
            sx={{
                width: 'var(--Dropdown-menuWidth, 220px)',
                minWidth: 'unset',
                maxWidth: 'unset',
                p: '4px',
                borderRadius: '20px',
                boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.06), 0px -6px 12px 0px rgba(0,0,0,0.03), 0px 14px 28px 0px rgba(0,0,0,0.08)',
                '--ListDivider-gap': '0px',
                // Cap the menu to the space below the trigger (which sits ~near the top of the sidebar)
                // and scroll internally. With many companies the list would otherwise overflow the
                // viewport bottom; bounding it keeps every item — active company and "Add New Company"
                // included — reachable without a window resize.
                maxHeight: 'calc(100dvh - 140px)',
                overflowY: 'auto',
            }}
        >
            {switchError && (
                <div className="px-3 py-1.5">
                    <p className="text-[12px] text-red-500">{switchError}</p>
                </div>
            )}
            <div style={{ padding: '10px 12px 4px' }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--joy-palette-text-tertiary)' }}>
                    {switching ? 'Switching...' : 'Switch account'}
                </span>
            </div>
            {applications.map((app) => {
                const isActive = app.id === currentAppId
                const name = app.company?.name || app.projectName
                const color = getAvatarColor(name)
                return (
                    <MenuItem
                        key={app.id}
                        onClick={() => handleSwitch(app.id)}
                        sx={{
                            borderRadius: '14px',
                            py: '8px',
                            px: '10px',
                            minHeight: '40px',
                            bgcolor: isActive ? 'rgba(4, 133, 247, 0.15) !important' : 'transparent',
                            '&:hover, &:focus-visible': {
                                bgcolor: isActive ? 'rgba(4, 133, 247, 0.15) !important' : 'rgba(128,128,128,0.12) !important',
                            },
                            cursor: isActive ? 'default' : 'pointer',
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                            <div style={{ width: 20, height: 20, borderRadius: 10, flexShrink: 0, backgroundColor: color }} />
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <div
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 500,
                                        color: 'var(--joy-palette-text-primary)',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    {name}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 5,
                                        fontSize: 11,
                                        color: 'var(--joy-palette-text-tertiary)',
                                        lineHeight: 1.3,
                                    }}
                                >
                                    <span>MC: {app.company?.mcNumber || 'Not available'}</span>
                                    <McStateDot state={mcVerificationState(isActive ? liveCompany : app.company)} />
                                    {mcVerificationState(isActive ? liveCompany : app.company) === McState.Unverified && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (isActive) window.location.assign('/settings?verifyMc=1')
                                                else handleSwitch(app.id, '/settings?verifyMc=1')
                                            }}
                                            style={{
                                                border: 'none',
                                                background: 'rgba(245, 158, 11, 0.15)',
                                                color: '#b45309',
                                                fontSize: 10,
                                                fontWeight: 600,
                                                padding: '1px 6px',
                                                borderRadius: 6,
                                                cursor: 'pointer',
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            Verify
                                        </button>
                                    )}
                                    {mcVerificationState(isActive ? liveCompany : app.company) === McState.Pending && (
                                        <span
                                            style={{
                                                background: 'rgba(59, 130, 246, 0.15)',
                                                color: '#1d4ed8',
                                                fontSize: 10,
                                                fontWeight: 600,
                                                padding: '1px 6px',
                                                borderRadius: 6,
                                                lineHeight: 1.4,
                                            }}
                                        >
                                            In review
                                        </span>
                                    )}
                                </div>
                            </div>
                            {isActive && <CheckIcon sx={{ fontSize: 14, color: 'var(--joy-palette-text-primary)' }} />}
                        </div>
                    </MenuItem>
                )
            })}
            {addState !== 'hidden' && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '2px 0' }}>
                        <div style={{ height: 1, width: 164, background: 'var(--joy-palette-divider)' }} />
                    </div>
                    <MenuItem
                        onClick={() => (addState === 'upgrade' ? window.location.assign('/billing') : setAddModalOpen(true))}
                        sx={{
                            borderRadius: '14px',
                            py: '8px',
                            px: '10px',
                            minHeight: 'unset',
                            '&:hover, &:focus-visible': { bgcolor: 'rgba(128,128,128,0.12) !important' },
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 16,
                                    flexShrink: 0,
                                    color: 'var(--joy-palette-text-tertiary)',
                                }}
                            >
                                {addState === 'upgrade' ? (
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <rect x="3" y="6.2" width="8" height="5.3" rx="1.3" stroke="currentColor" strokeWidth="1.4" />
                                        <path d="M4.6 6.2V4.8a2.4 2.4 0 0 1 4.8 0v1.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                                    </svg>
                                ) : (
                                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                        <circle cx="7" cy="7" r="6" stroke="currentColor" strokeWidth="1.5" />
                                        <path d="M7 4.5V9.5M4.5 7H9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                    </svg>
                                )}
                            </div>
                            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--joy-palette-text-primary)', flex: 1 }}>Add New Company</span>
                            {addState === 'upgrade' && (
                                <span
                                    style={{
                                        background: 'rgba(245, 158, 11, 0.15)',
                                        color: '#b45309',
                                        fontSize: 10,
                                        fontWeight: 600,
                                        padding: '1px 6px',
                                        borderRadius: 6,
                                        lineHeight: 1.4,
                                        flexShrink: 0,
                                    }}
                                >
                                    Upgrade
                                </span>
                            )}
                        </div>
                    </MenuItem>
                </>
            )}
        </Menu>
    )

    return (
        <>
            <Dropdown>
                {collapsed ? (
                    <MenuButton
                        variant="plain"
                        color="neutral"
                        size="sm"
                        disabled={switching}
                        sx={{
                            width: '100%',
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: '8px',
                            borderRadius: '12px',
                            border: 'none',
                            bgcolor: getAvatarColor(companyName),
                            boxShadow: 'none',
                            '&:hover': { bgcolor: getAvatarColor(companyName), opacity: 0.9, boxShadow: 'none' },
                            '&:focus': { boxShadow: 'none' },
                            minWidth: 'unset',
                        }}
                    >
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#18181b', lineHeight: '20px' }}>{companyName.charAt(0).toUpperCase()}</span>
                    </MenuButton>
                ) : (
                    <MenuButton
                        variant="plain"
                        color="neutral"
                        size="sm"
                        disabled={switching}
                        sx={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 1,
                            px: '13px',
                            py: '9px',
                            borderRadius: '16px',
                            border: 'none',
                            bgcolor: getAvatarColor(companyName),
                            boxShadow: 'none',
                            '&:hover': { bgcolor: getAvatarColor(companyName), opacity: 0.9, boxShadow: 'none' },
                            '&:focus': { boxShadow: 'none' },
                            transition: 'opacity 0.15s',
                        }}
                    >
                        <div className="min-w-0 flex-1 text-left">
                            <p className="text-[14px] font-semibold text-[#18181b] truncate leading-[20px]">{companyName}</p>
                            <p className="flex items-center gap-1.5 text-[12px] truncate leading-[16px]" style={{ color: 'rgba(24,24,27,0.6)' }}>
                                <span>MC: {mcNumber || 'Not available'}</span>
                                <McStateDot state={mcVerificationState(liveCompany)} />
                            </p>
                        </div>
                        <span style={{ color: '#18181b', opacity: 0.6, display: 'flex' }}>
                            <ChevronUpDown size={16} />
                        </span>
                    </MenuButton>
                )}
                {menuContent}
            </Dropdown>
            <AddCompanyModal open={addModalOpen} onClose={() => setAddModalOpen(false)} />
        </>
    )
}
